import { useStatefulHistory } from 'hooks/history';
import { useUrl } from 'hooks/useUrls';
import { getConsistencyReportUrl } from 'utils/router-url';
import { parseFilterToLogicalRule, LogicalRule } from 'utils/rules';
import { getConditionsCount } from 'utils/project-ruleset';
import { calculateSuccessRate } from '../helpers';
import {
  FailedInstance,
  ConsistencyRuleRunResult,
} from '../consistencyReportTypes';
import {
  ConditionOperatorLabel,
  conditionOperatorNeedsValue,
} from '../../DataConsistencyExplorer/types';
import { useConsistencyReport } from './useConsistencyReport';
import { useConsistencyFailedInstances } from './useConsistencyFailedInstances';

type ConditionCheckLabel = { key: string; label: string };

const formatConditionChecks = (
  rule: ConsistencyRuleRunResult
): ConditionCheckLabel[] =>
  rule.conditionChecks.map((check) => {
    const schemaPrefix =
      check.isLiteralValue && check.useTargetSchema
        ? rule.targetSchemaFilterName
        : rule.sourceSchemaFilterName;
    const operatorLabel = ConditionOperatorLabel[check.operator];
    let suffix = '';
    if (conditionOperatorNeedsValue(check.operator) && check.targetAttribute) {
      suffix = check.isLiteralValue
        ? ` "${check.targetAttribute}"`
        : ` ${rule.targetSchemaFilterName}.${check.targetAttribute}`;
    }
    return {
      key: check.externalId,
      label: `${schemaPrefix}.${check.sourceAttribute} ${operatorLabel}${suffix}`,
    };
  });

const parseFilterTree = (filters: string | null): LogicalRule | null => {
  if (!filters) return null;
  const tree = parseFilterToLogicalRule(filters);
  return getConditionsCount(tree).valid > 0 ? tree : null;
};

const downloadFailedInstancesJson = (
  ruleName: string,
  instances: FailedInstance[]
) => {
  const blob = new Blob([JSON.stringify(instances, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${ruleName || 'rule'}-failed-instances.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const useConsistencyRuleReportPage = () => {
  const consistencyReportId = useUrl.ConsistencyReportId();
  const digitalTwinId = useUrl.DigitalTwinId();
  const tenant = useUrl.Tenant();
  const ruleId = useUrl.RuleId();
  const history = useStatefulHistory();

  const { report, loading, error } = useConsistencyReport(
    consistencyReportId,
    digitalTwinId
  );

  const rule = report?.ruleResults.find((r) => r.ruleId === ruleId) ?? null;

  const sourceFilterTree = rule ? parseFilterTree(rule.sourceFilters) : null;
  const targetFilterTree = rule ? parseFilterTree(rule.targetFilters) : null;
  const conditionCheckLabels = rule ? formatConditionChecks(rule) : [];

  const {
    instances,
    loading: instancesLoading,
    error: instancesError,
  } = useConsistencyFailedInstances(rule?.runStatusId ?? null, rule?.status);

  const successRate = rule
    ? calculateSuccessRate(rule.totalValid, rule.totalFailed)
    : 0;

  const handleGoBack = () => {
    if (consistencyReportId && digitalTwinId && tenant) {
      history.pushStateful(
        getConsistencyReportUrl(tenant, digitalTwinId, consistencyReportId)
      );
    } else {
      history.goBack();
    }
  };

  const handleDownload = () => {
    if (rule) downloadFailedInstancesJson(rule.ruleName, instances);
  };

  return {
    report,
    rule,
    loading,
    error,
    instances,
    instancesLoading,
    instancesError,
    successRate,
    ruleId,
    consistencyReportId,
    sourceFilterTree,
    targetFilterTree,
    conditionCheckLabels,
    handleGoBack,
    handleDownload,
  };
};
